// Lot 678 — cache partagé des aperçus de liens (fil, commentaires, messages) :
//   • mémoire : une URL n'est demandée qu'une fois par session, les demandes identiques en vol sont fusionnées ;
//   • appareil (localStorage) : le résultat est gardé 24 h (lien sans aperçu : 2 h) → au prochain lancement, aucune
//     requête pour un lien déjà vu — fil plus léger, données mobiles préservées ;
//   • 2 requêtes réseau max en parallèle : un fil plein de liens ne sature ni le réseau ni le backend et ne concurrence
//     jamais la vidéo en cours de lecture (les suivantes attendent leur tour, dans l'ordre d'apparition).
import { api } from "@/lib/api";

const KEY = "yalola.linkpv";
export const TTL_HIT_MS = 24 * 60 * 60 * 1000;  // aperçu trouvé : 24 h
export const TTL_MISS_MS = 2 * 60 * 60 * 1000;  // lien sans aperçu : 2 h (le site peut ajouter ses balises plus tard)
export const MAX_CONCURRENT = 2;
const MAX_STORED = 150; // les 150 liens les plus récents seulement : le stockage de l'appareil reste petit

const mem = new Map();      // url → aperçu | null (null = « ce lien n'a pas d'aperçu »)
const inflight = new Map(); // url → Promise en cours
const readStore = () => { try { const v = JSON.parse(localStorage.getItem(KEY) || "null"); return v && typeof v === "object" ? v : {}; } catch { return {}; } };
let store = readStore();
const persist = () => {
  try {
    // Les plus récents d'abord, puis coupe à MAX_STORED entrées.
    store = Object.fromEntries(Object.entries(store).sort((a, b) => (b[1].t || 0) - (a[1].t || 0)).slice(0, MAX_STORED));
    localStorage.setItem(KEY, JSON.stringify(store));
  } catch { /* quota plein / navigation privée : le cache mémoire suffit */ }
};
const fresh = (e, now) => !!e && now - (e.t || 0) < (e.pv ? TTL_HIT_MS : TTL_MISS_MS);

// Lecture SYNCHRONE (mémoire, puis appareil) : la carte s'affiche dès le premier rendu, sans « clignotement ».
// `undefined` = inconnu (il faudra demander) ; `null` = connu sans aperçu.
export const getCachedLinkPreview = (url, now = Date.now()) => {
  if (mem.has(url)) return mem.get(url);
  const e = store[url];
  if (fresh(e, now)) { mem.set(url, e.pv); return e.pv; }
  return undefined;
};
const remember = (url, pv) => { mem.set(url, pv); store[url] = { pv, t: Date.now() }; persist(); };

// Petite file d'attente : au plus MAX_CONCURRENT requêtes réseau en même temps.
let running = 0;
const waiting = [];
const next = () => { while (running < MAX_CONCURRENT && waiting.length) { running++; waiting.shift()(); } };
const schedule = (job) => new Promise((resolve) => {
  // Une erreur réseau devient `null` (pas d'aperçu) : jamais d'exception vers l'interface.
  waiting.push(() => job().then(resolve, () => resolve(null)).finally(() => { running--; next(); }));
  next();
});

export const fetchLinkPreview = (url) => {
  const hit = getCachedLinkPreview(url);
  if (hit !== undefined) return Promise.resolve(hit);
  if (inflight.has(url)) return inflight.get(url);
  const p = schedule(() => api.get("/link-preview", { params: { url } }).then(({ data }) => data?.preview || null))
    .then((pv) => { remember(url, pv); return pv; })
    .finally(() => inflight.delete(url));
  inflight.set(url, p);
  return p;
};

export const _resetLinkPreviewCacheForTests = () => {
  mem.clear(); inflight.clear(); store = {}; running = 0; waiting.length = 0;
  try { localStorage.removeItem(KEY); } catch { /* noop */ }
};
